import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { ComplianceStatus } from '@prisma/client';
import { useEffect } from 'react';
import { AddAssigneeModal } from './AddAssigneeModal';
import { Assignee } from './Assignee';

const ManageAssigneesModal = ({
  isOpen,
  onClose,
  filing,
  refreshCompliances,
  loggedInUserEmail,
}: {
  isOpen: boolean;
  onClose: () => void;
  filing: TComplianceItem;
  refreshCompliances: () => void;
  loggedInUserEmail: string;
}) => {
  const currentModalVisibility = useToggle();
  const addAssigneeModalVisibility = useToggle();

  const handleClose = () => {
    currentModalVisibility.off();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      currentModalVisibility.on();
    } else {
      currentModalVisibility.off();
    }
  }, [isOpen]);

  const assignedAccountant = filing.assignedAccountant?.name;

  const assignees = filing.assignees || [];

  const isFilingCompletedOrFiledOrArchived =
    filing.status === ComplianceStatus.COMPLETED ||
    filing.status === ComplianceStatus.FILED ||
    filing.status === ComplianceStatus.ARCHIVED;

  return (
    <>
      <Dialog open={currentModalVisibility.visible} onOpenChange={handleClose}>
        <DialogContent
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          preventCloseOnOutsideClick
        >
          <DialogHeader className="flex flex-col gap-xs items-start">
            <DialogTitle>
              {isFilingCompletedOrFiledOrArchived
                ? 'View assignees'
                : 'Manage assignees'}
            </DialogTitle>
            <DialogDescription>
              View {isFilingCompletedOrFiledOrArchived ? '' : 'and re-assign'}{' '}
              team members to <span className="font-bold">{filing.title}</span>{' '}
              for {filing.corporation.companyDisplayName}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4xl p-3xl">
            {assignedAccountant && (
              <div className="flex flex-col gap-lg">
                <span className="txt-small-bold">Accounting partner</span>
                <div className="border border-border-primary rounded-md py-lg px-xl flex items-center gap-lg">
                  <Avatar size="sm" name={assignedAccountant} />
                  <span className="txt-small-bold">{assignedAccountant}</span>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-lg">
              <span className="txt-small-bold">Internal assignees</span>
              {!isFilingCompletedOrFiledOrArchived ? (
                <Button
                  size="sm"
                  variant="link"
                  className="text-utility-blue-600 hover:text-utility-blue-700"
                  onClick={() => {
                    currentModalVisibility.off();
                    addAssigneeModalVisibility.on();
                  }}
                >
                  <Icons.Plus className="size-4" />
                  <span className="txt-small-medium">Add assignees</span>
                </Button>
              ) : null}

              {assignees.length
                ? assignees.map((assignee) => (
                    <div
                      key={assignee.id}
                      className="flex flex-col overflow-clip rounded-md border border-border-primary divide-y divide-border-primary"
                    >
                      <Assignee
                        assignee={assignee}
                        filing={filing}
                        refreshCompliances={refreshCompliances}
                        hideDeleteAssignee={assignees.length === 1}
                        loggedInUserEmail={loggedInUserEmail}
                      />
                    </div>
                  ))
                : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AddAssigneeModal
        isOpen={addAssigneeModalVisibility.visible}
        onClose={() => {
          addAssigneeModalVisibility.off();
          currentModalVisibility.on();
        }}
        filing={filing}
        onSuccess={refreshCompliances}
      />
    </>
  );
};

export { ManageAssigneesModal };
